import { ShoppingBag, X } from "lucide-react";
import { useEffect, useState } from "react";
import { checkout } from "../lib/api";
import { useCart } from "../lib/cart";
import { formatCurrency } from "../lib/format";
import { Button } from "./Button";

export function CartDrawer() {
  const cart = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.classList.toggle("cart-drawer-active", cart.open);
    if (!cart.open) return () => document.body.classList.remove("cart-drawer-active");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cart.setOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("cart-drawer-active");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [cart.open, cart.setOpen]);

  async function handleCheckout() {
    if (!cart.items.length || checkingOut) return;
    setCheckingOut(true);
    setError("");
    try {
      const response = await checkout({ items: cart.items });
      window.location.href = response.checkoutUrl;
    } catch {
      setError("Checkout could not open. Please try again.");
      setCheckingOut(false);
    }
  }

  return (
    <>
      <div
        className={`cart-backdrop ${cart.open ? "cart-backdrop-open" : ""}`}
        aria-hidden="true"
        onClick={() => cart.setOpen(false)}
      />
      <aside
        className={`cart-drawer ${cart.open ? "cart-drawer-open" : ""}`}
        aria-hidden={!cart.open}
        inert={!cart.open ? true : undefined}
        data-testid="cart-drawer"
      >
        <div className="cart-header">
          <div>
            <p className="eyebrow">Your order</p>
            <h2>Bistro Cart</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close cart" onClick={() => cart.setOpen(false)}>
            <X size={20} />
          </button>
        </div>
        {cart.items.length ? (
          <div className="cart-items">
            {cart.items.map((item) => (
              <article key={`${item.kind}-${item.id}`} className="cart-item">
                <img src={item.image} alt={item.name} />
                <div>
                  <h3>{item.name}</h3>
                  <p>{item.subtitle}</p>
                  <strong>{formatCurrency(item.priceCents)}</strong>
                  <div className="quantity-controls">
                    <button
                      type="button"
                      aria-label={`Decrease ${item.name}`}
                      onClick={() => cart.updateQuantity(item.id, item.kind, item.quantity - 1)}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      type="button"
                      aria-label={`Increase ${item.name}`}
                      onClick={() => cart.updateQuantity(item.id, item.kind, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-cart">
            <ShoppingBag size={36} />
            <p>Your cart is ready for something delicious.</p>
          </div>
        )}
        <div className="cart-footer">
          <div>
            <span>Total</span>
            <strong>{formatCurrency(cart.totalCents)}</strong>
          </div>
          <Button data-testid="checkout-button" onClick={handleCheckout} disabled={!cart.items.length || checkingOut}>
            {checkingOut ? "Opening checkout..." : "Checkout"}
          </Button>
        </div>
        {error ? <p className="cart-error">{error}</p> : null}
      </aside>
    </>
  );
}
